import React, { memo } from "react";
import { Box, Paper, useTheme, alpha } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { ApiEndpoint } from "../../types";
import IntelligentFieldDisplay from "./IntelligentFieldDisplay";

interface AdditionalDetailsPanelProps {
  /** The data item to display additional details for */
  item: any;
  /** API endpoint configuration for data source awareness */
  endpoint?: ApiEndpoint;
  /** Map popup: no inner card — full-width minimal rows on slate header */
  embeddedDark?: boolean;
  /** Custom styling overrides */
  sx?: any;
}

const scrollSx = (embeddedDark: boolean, theme: Theme) => ({
  maxHeight: "500px",
  overflowY: "auto" as const,
  width: "100%",
  "&::-webkit-scrollbar": {
    width: "6px"
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent"
  },
  "&::-webkit-scrollbar-thumb": {
    background: embeddedDark ? "rgba(255, 255, 255, 0.22)" : alpha(theme.palette.text.secondary, 0.15),
    borderRadius: "10px"
  },
  "&::-webkit-scrollbar-thumb:hover": {
    background: embeddedDark ? "rgba(255, 255, 255, 0.32)" : alpha(theme.palette.text.secondary, 0.25)
  }
});

const AdditionalDetailsPanel: React.FC<AdditionalDetailsPanelProps> = memo(({ item, endpoint, embeddedDark = false, sx = {} }) => {
  const theme = useTheme();

  if (!item) return null;

  if (embeddedDark) {
    return (
      <Box
        sx={{
          mt: 1,
          mx: 0,
          alignSelf: "stretch",
          ...scrollSx(true, theme),
          ...sx
        }}
      >
        <IntelligentFieldDisplay item={item} endpoint={endpoint} mode='full' surface='embeddedDark' />
      </Box>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 1,
        backgroundColor: alpha(theme.palette.background.paper, 0.55),
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        borderRadius: 3,
        overflow: "hidden",
        boxShadow: `0 2px 12px ${alpha(theme.palette.common.black, 0.04)}`,
        ...sx
      }}
    >
      <Box
        sx={{
          p: 1.25,
          ...scrollSx(false, theme)
        }}
      >
        <IntelligentFieldDisplay item={item} endpoint={endpoint} mode='full' surface='panel' />
      </Box>
    </Paper>
  );
});

AdditionalDetailsPanel.displayName = "AdditionalDetailsPanel";

export default AdditionalDetailsPanel;
